"use client";
import Image from "next/image";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import CloudsAnimation from "../common/CloudsAnimation";
import { useEffect, useRef } from "react";
gsap.registerPlugin(ScrollTrigger);

const RoadMap = () => {
  // ANIMATION
  const bodyContainer = useRef<HTMLDivElement | null>(null);
  const airplane = useRef<HTMLDivElement | null>(null);
  const academicDetailsRef = useRef<HTMLDivElement | null>(null);
  const proficiencyDetailsRef = useRef<HTMLDivElement | null>(null);
  const englishProficiencyDetailsRef = useRef<HTMLDivElement | null>(null);
  const resultRef = useRef<HTMLDivElement | null>(null);
  const applyRef = useRef<HTMLDivElement | null>(null);
  const pickPreviousQualificationRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      !bodyContainer.current ||
      !airplane.current ||
      !pickPreviousQualificationRef.current ||
      !academicDetailsRef.current ||
      !proficiencyDetailsRef.current ||
      !englishProficiencyDetailsRef.current ||
      !resultRef.current ||
      !applyRef.current
    ) {
      console.warn("One or more elements are missing!");
      return;
    }

    const container = bodyContainer.current;
    const airplaneEl = airplane.current;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: "top top",
        end: "+=6000", // adjust scroll length if needed
        scrub: true,
        pin: true,
      },
    });

    // STEP 1
    tl.fromTo(
      pickPreviousQualificationRef.current,
      { opacity: 0, y: 100 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        onStart: () => pickPreviousQualificationRef.current?.classList.remove("opacity-0"),
        onReverseComplete: () =>
          pickPreviousQualificationRef.current?.classList.add("opacity-0"),
      },
    ).to(pickPreviousQualificationRef.current, { opacity: 0, duration: 1 });

    // STEP 2
    tl.fromTo(
      academicDetailsRef.current,
      { opacity: 0, y: 100 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        onStart: () =>
          academicDetailsRef.current?.classList.remove("opacity-0"),
        onReverseComplete: () =>
          academicDetailsRef.current?.classList.add("opacity-0"),
      },
      "-=0.3",
    ).to(academicDetailsRef.current, { opacity: 0, duration: 1 }, "-=0.2");

    // STEP 3
    tl.fromTo(
      proficiencyDetailsRef.current,
      { opacity: 0, y: 100 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        onStart: () =>
          proficiencyDetailsRef.current?.classList.remove("opacity-0"),
        onReverseComplete: () =>
          proficiencyDetailsRef.current?.classList.add("opacity-0"),
      },
      "-=0.3",
    ).to(proficiencyDetailsRef.current, { opacity: 0, duration: 1 }, "-=0.2");

    // STEP 4 (reduced time between 3 → 4)
    tl.fromTo(
      englishProficiencyDetailsRef.current,
      { opacity: 0, y: 100 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        onStart: () =>
          englishProficiencyDetailsRef.current?.classList.remove("opacity-0"),
        onReverseComplete: () =>
          englishProficiencyDetailsRef.current?.classList.add("opacity-0"),
      },
      "-=0.4",
    ).to(
      englishProficiencyDetailsRef.current,
      { opacity: 0, duration: 1 },
      "-=0.3",
    );

    // STEP 5 (reduced time between 4 → 5)
    tl.fromTo(
      resultRef.current,
      { opacity: 0, y: 100 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        onStart: () => resultRef.current?.classList.remove("opacity-0"),
        onReverseComplete: () => resultRef.current?.classList.add("opacity-0"),
      },
      "-=0.4",
    ).to(resultRef.current, { opacity: 0, duration: 1 }, "-=0.3");

    // STEP 6
    tl.fromTo(
      applyRef.current,
      { opacity: 0, y: 100 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        onStart: () => applyRef.current?.classList.remove("opacity-0"),
        onReverseComplete: () => applyRef.current?.classList.add("opacity-0"),
      },
      "-=0.3",
    );

    // AIRPLANE ANIMATION
    gsap.to(airplaneEl, {
      y: "85vh",
      ease: "none",
      scrollTrigger: {
        trigger: container,
        start: "top top",
        end: "+=6000",
        scrub: true,
      },
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div className="mt-[90px] relative">
      <div className="absolute w-full top-[2%] left-0 overflow-hidden hidden sm:block">
        <CloudsAnimation />
      </div>
      <p className="text-center font-extrabold text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl 3xl:text-5xl pb-[165px] mx-6">
        With the help of our smart Course Finder, you can:
      </p>
      <div className="relative h-[100vh]" ref={bodyContainer}>
        <span className="inline-block max-w-[40px] sm:max-w-[50px] md:max-w-[60px] lg:max-w-[80px] h-[calc(100vh-90px)] sticky top-[80px] left-[31px] sm:left-[40px] md:left-[50px] lg:left-1/2 -translate-x-1/2">
          <Image
            className="w-full h-full"
            src={"/assets/images/png/applicationAssistance/track.png"}
            alt="track"
            width={100}
            height={1200}
          />
        </span>
        <span
          ref={airplane}
          className="absolute left-[32px] sm:left-[40px] md:left-[50px] max-w-[50px] sm:max-w-[60px] md:max-w-[60px] lg:left-1/2 lg:max-w-[100px] top-0 -translate-x-1/2 z-10"
        >
          <Image
            src={"/assets/images/png/applicationAssistance/airplane.png"}
            alt="airplane"
            width={150}
            height={1200}
          />
        </span>
        {/* PICK COUNTRY STEP */}
        <div
          ref={pickPreviousQualificationRef}
          className="flex lg:items-center flex-col lg:flex-row justify-center gap-5 lg:gap-0 lg:justify-between pl-16 sm:pl-20 md:pl-24 pr-5 lg:px-14 fixed top-0 w-full h-[calc(100vh-72px)] pt-14 lg:pt-0"
        >
          <div className="3xl:pl-20 lg:w-[45%]">
            <p className="font-extrabold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 3xl:text-5xl text-black mb-3 lg:leading-9 xl:leading-10 2xl:leading-12 3xl:leading-16">
              Step 1:
              <br />
              Select Your Past Qualification
            </p>
            <p className="ff_poppins font-medium text-sm sm:text-base md:text-lg xl:text-xl 3xl:text-2xl max-w-[490px]">
              Tell us your current education level - Class 12th/Plus Two, Bachelor's Degree, Master's Degree, Diploma, or Others - so we can curate degree options tailored specifically to your academic journey.
            </p>
          </div>
          <div className="w-full lg:max-w-[45%]">
            <Image
              className="hidden sm:block w-full shadow-[0px_4px_12px_0px_#00000040]"
              src="/assets/images/png/aiCourseFinder/newPickPreviousQualification.png"
              alt="step1"
              width={841}
              height={480}
            />
            <Image
              className="block sm:hidden w-[90%] mx-auto shadow-[0px_4px_12px_0px_#00000040]"
              src="/assets/images/png/aiCourseFinder/newPickPreviousQualificationMobile.png"
              alt="step1 mobile"
              width={841}
              height={480}
            />
          </div>
        </div>
        {/* ACADEMIC DETAILS STEP */}
        <div
          ref={academicDetailsRef}
          className="flex lg:items-center flex-col lg:flex-row justify-center gap-5 lg:gap-0 lg:justify-between pl-16 sm:pl-20 md:pl-24 pr-5 lg:px-14 fixed top-0 w-full h-[calc(100vh-72px)] pt-14 lg:pt-0"
        >
          <div className="3xl:pl-20 lg:w-[45%]">
            <p className="font-extrabold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 3xl:text-5xl text-black mb-3 lg:leading-9 xl:leading-10 2xl:leading-12 3xl:leading-16">
              Step 1:
              <br />
              Select Your Qualification Stream
            </p>
            <p className="ff_poppins font-medium text-sm sm:text-base md:text-lg xl:text-xl 3xl:text-2xl max-w-[490px]">
              Choose the stream that matches your academic background - Arts & Humanities, Commerce, Medical, Non-Medical, and more - so we can align courses with your unique background.
            </p>
          </div>
          <div className="w-full lg:max-w-[45%]">
            <Image
              className="hidden sm:block w-full shadow-[0px_4px_12px_0px_#00000040]"
              src="/assets/images/png/aiCourseFinder/newAcademicDetails.png"
              alt="step1"
              width={841}
              height={473}
            />
            <Image
              className="block sm:hidden w-[90%] mx-auto shadow-[0px_4px_12px_0px_#00000040]"
              src="/assets/images/png/aiCourseFinder/newAcademicDetailsMobile.png"
              alt="step1 mobile"
              width={841}
              height={473}
            />
          </div>
        </div>
        {/* PROFICIENCY DETAILS STEP */}
        <div
          ref={proficiencyDetailsRef}
          className="flex lg:items-center flex-col lg:flex-row justify-center gap-5 lg:gap-0 lg:justify-between pl-16 sm:pl-20 md:pl-24 pr-5 lg:px-14 fixed top-0 w-full h-[calc(100vh-72px)] pt-14 lg:pt-0"
        >
          <div className="3xl:pl-20 lg:w-[45%]">
            <p className="font-extrabold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 3xl:text-5xl text-black mb-3 lg:leading-9 xl:leading-10 2xl:leading-12 3xl:leading-16">
              Step 1:
              <br />
              Enter Your Pass-Out Year & Percentage
            </p>
            <p className="ff_poppins font-medium text-sm sm:text-base md:text-lg xl:text-xl 3xl:text-2xl max-w-[490px]">
              Share your graduation year and overall academic score so we can handpick universities and courses that match your profile, eligibility, and timeline.
            </p>
          </div>
          <div className="w-full lg:max-w-[45%]">
            <Image
              className="hidden sm:block w-full shadow-[0px_4px_12px_0px_#00000040]"
              src="/assets/images/png/aiCourseFinder/passoutYear.png"
              alt="step1"
              width={841}
              height={473}
            />
            <Image
              className="block sm:hidden w-[90%] mx-auto shadow-[0px_4px_12px_0px_#00000040]"
              src="/assets/images/png/aiCourseFinder/passoutYearMobile.png"
              alt="step1 mobile"
              width={841}
              height={473}
            />
          </div>
        </div>
        {/* ENGLISH PROFICIENCY DETAILS STEP */}
        <div
          ref={englishProficiencyDetailsRef}
          className="flex lg:items-center flex-col lg:flex-row justify-center gap-5 lg:gap-0 lg:justify-between pl-16 sm:pl-20 md:pl-24 pr-5 lg:px-14 fixed top-0 w-full h-[calc(100vh-72px)] pt-14 lg:pt-0"
        >
          <div className="3xl:pl-20 lg:w-[45%]">
            <p className="font-extrabold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 3xl:text-5xl text-black mb-3 lg:leading-9 xl:leading-10 2xl:leading-12 3xl:leading-16">
              Step 1:
              <br />
              Enter Your English Proficiency Details
            </p>
            <p className="ff_poppins font-medium text-sm sm:text-base md:text-lg xl:text-xl 3xl:text-2xl max-w-[490px]">
              Select your test - IELTS, PTE, TOEFL, or Duolingo - (or choose "I don't have any") and enter your score. Click Continue to get matched with courses & universities you're eligible for.
            </p>
          </div>
          <div className="w-full lg:max-w-[45%]">
            <Image
              className="hidden sm:block w-full shadow-[0px_4px_12px_0px_#00000040]"
              src="/assets/images/png/aiCourseFinder/newEnglishProficieny.png"
              alt="step1"
              width={841}
              height={473}
            />
            <Image
              className="block sm:hidden w-[90%] mx-auto shadow-[0px_4px_12px_0px_#00000040]"
              src="/assets/images/png/aiCourseFinder/newEnglishProficienyMobile.png"
              alt="step1 mobile"
              width={841}
              height={473}
            />
          </div>
        </div>
        {/* RESULT STEP */}
        <div
          ref={resultRef}
          className="flex lg:items-center flex-col lg:flex-row justify-center gap-5 lg:gap-0 lg:justify-between pl-16 sm:pl-20 md:pl-24 pr-5 lg:px-14 fixed top-0 w-full h-[calc(100vh-72px)] pt-14 lg:pt-0"
        >
          <div className="3xl:pl-20 lg:w-[45%]">
            <p className="font-extrabold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 3xl:text-5xl text-black mb-3 lg:leading-9 xl:leading-10 2xl:leading-12 3xl:leading-16">
              Step 2:
              <br />
              Get personalised matches
            </p>
            <p className="ff_poppins font-medium text-sm sm:text-base md:text-lg xl:text-xl 3xl:text-2xl max-w-[490px]">
              Based on the details you've entered, your AI Mentor hand-picks university and course recommendations you're eligible for - each with a fit score, fees, job rate, ROI, and honest reasoning for why it suits you.
            </p>
          </div>
          <div className="w-full lg:max-w-[45%]">
            <Image
              className="hidden sm:block w-full shadow-[0px_4px_12px_0px_#00000040]"
              src="/assets/images/png/aiCourseFinder/newResult.png"
              alt="step1"
              width={841}
              height={473}
            />
            <Image
              className="block sm:hidden w-[90%] mx-auto shadow-[0px_4px_12px_0px_#00000040]"
              src="/assets/images/png/aiCourseFinder/newResultMobile.png"
              alt="step1 mobile"
              width={841}
              height={473}
            />
          </div>
        </div>

        {/* APPLY STEP */}
        <div
          ref={applyRef}
          className="flex lg:items-center flex-col lg:flex-row justify-center gap-5 lg:gap-0 lg:justify-between pl-16 sm:pl-20 md:pl-24 pr-5 lg:px-14 fixed top-0 w-full h-[calc(100vh-72px)] pt-14 lg:pt-0"
        >
          <div className="3xl:pl-20 lg:w-[45%]">
            <p className="font-extrabold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 3xl:text-5xl text-black mb-3 lg:leading-9 xl:leading-10 2xl:leading-12 3xl:leading-16">
              Step 3:
              <br />
              Apply easily
            </p>
            <p className="ff_poppins font-medium text-sm sm:text-base md:text-lg xl:text-xl 3xl:text-2xl max-w-[490px]">
              Once you find a course you like, just click "Apply," select your campus and intake, upload the required documents, and follow the simple guided steps - all the way to your offer letter.
            </p>
          </div>
          <div className="w-full lg:max-w-[45%]">
            <Image
              className="hidden sm:block w-full shadow-[0px_4px_12px_0px_#00000040]"
              src="/assets/images/png/aiCourseFinder/newApply.png"
              alt="step1"
              width={841}
              height={473}
            />
            <Image
              className="block sm:hidden w-[90%] mx-auto shadow-[0px_4px_12px_0px_#00000040]"
              src="/assets/images/png/aiCourseFinder/newApplyMobile.png"
              alt="step1 mobile"
              width={841}
              height={473}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
export default RoadMap;
